import type { FC, ReactElement } from 'react';
import React, { Children, cloneElement } from 'react';
import cn from 'classnames';

import type { TList, TSeoTypes } from './models';

const DEFAULT_MARKER_COLOR = '#EF3124'; // accent/primary
const INVERTED_MARKER_COLOR = '#F83A2A'; // accent_inverted/primary

export const List: FC<TList> = ({
    children,
    className,
    horizontal = false,
    indentType = 'default',
    inverted,
    itemClassName,
    marginCollapseFirst = false,
    marginCollapseLast = false,
    marginCollapseVertical = false,
    marginHorizontal = false,
    marker = '•',
    markerColor,
    nowrap = false,
    olRedMarker = false,
    olType,
    styles,
    svgHeight = 'auto',
    svgMargin = '0 8px 0 0',
    svgSrc = '',
    svgWidth = 'auto',
    type = 'div',
}) => {
    let ListParent: TSeoTypes;

    let Item: 'li' | 'div';

    const currentColor = markerColor
        ? markerColor
        : inverted
        ? INVERTED_MARKER_COLOR
        : DEFAULT_MARKER_COLOR;

    switch (type) {
        case 'ul': {
            ListParent = 'ul';
            Item = 'li';
            break;
        }

        case 'ol': {
            ListParent = 'ol';
            Item = 'li';
            break;
        }

        default: {
            ListParent = 'div';
            Item = 'div';
        }
    }

    return (
        <ListParent
            className={
                // eslint-disable-next-line external-classname/specificity
                cn(
                    styles.list,
                    { [styles.orderedList]: olType, [styles.listNowrap]: nowrap },
                    className
                )
            }
            type={olType}
        >
            {Children.toArray(children).map((child, index) => {
                return (
                    <Item
                        // eslint-disable-next-line react/no-array-index-key
                        key={`listItem-${index}`}
                        className={cn(
                            {
                                [styles.listItem]: !horizontal,
                                [styles.horizontalListItem]: horizontal,
                                // TODO: Когда будет выпелен indentType, нужно будет оставить только styles.marginDefault, сейчас он не применяется никогда, потому что indentType = 'default' по умолчанию
                                ...(indentType === undefined
                                    ? /* istanbul ignore next */
                                      { [styles.marginDefault]: true }
                                    : indentType === 'none'
                                    ? { indent_none: true }
                                    : {
                                          [styles[`indent_${indentType}`]]: indentType,
                                      }),
                                [styles.marginCollapseFirst]: marginCollapseFirst,
                                [styles.marginCollapseLast]: marginCollapseLast,
                                [styles.marginHorizontal]: marginHorizontal,
                                [styles.marginCollapseVertical]: marginCollapseVertical,
                                [styles.orderedListItem]: olType,
                                [styles.redMarker]: olRedMarker,
                            },
                            itemClassName
                        )}
                    >
                        {(marker || svgSrc) && (
                            <div
                                className={cn(styles.marker)}
                                style={{
                                    color: currentColor,
                                    margin: svgMargin,
                                    width: svgWidth,
                                    height: svgHeight,
                                }}
                            >
                                {svgSrc ? (
                                    <img
                                        alt="icon"
                                        src={svgSrc}
                                        style={{
                                            width: svgWidth,
                                            height: svgHeight,
                                        }}
                                    />
                                ) : (
                                    marker
                                )}
                            </div>
                        )}

                        {cloneElement(child as ReactElement, {
                            style: { margin: 0, padding: 0 },
                        })}
                    </Item>
                );
            })}
        </ListParent>
    );
};
